import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';
import type { EntryComponent } from 'cms/entries/entries';
import SpinningGradient from 'components/shared/SpinningGradient';
import type { ILpComponentHeaderStandalone } from 'declarations/contentful';

import ContextualField from './ContextualField';
import { ArchSVG } from './shared/ArchSVG';
import { LPLink } from './shared/Buttons';
import Container from './shared/Container';
import Sheet from './shared/Sheet';
import {
  BNColorThemes,
  getBNComponentThemeColors,
  getComponentThemeColors,
} from './shared/styles/ComponentTheme';
import type {
  IBNComponentThemeConfig,
  IComponentThemeOptions,
} from './shared/styles/ComponentTheme';
import { Desc, Eyebrow, Subhead, SubheadSmall, TitleWithAccent } from './shared/Typography';

// Background utility (and the matching CSS var for the ArchSVG fill) per theme.
// Keyed by the theme name so callers index with the BNColorThemes value directly.
// The class strings are literals so Tailwind generates them even though the lookup
// is dynamic.
const BG_CLASS: Record<BNColorThemes, string> = {
  [BNColorThemes.Light]: 'bg-od-bg-primary',
  [BNColorThemes.Dark]: 'bg-od-bg-inverse-primary',
  [BNColorThemes.Grey]: 'bg-od-bg-tertiary',
};
const BG_CSS_VAR: Record<BNColorThemes, string> = {
  [BNColorThemes.Light]: 'var(--color-od-bg-primary)',
  [BNColorThemes.Dark]: 'var(--color-od-bg-inverse-primary)',
  [BNColorThemes.Grey]: 'var(--color-od-bg-tertiary)',
};

interface CtaSheetProps {
  entry: ILpComponentHeaderStandalone;
}

const CtaSheet = ({ entry }: CtaSheetProps) => {
  const { fields } = entry;

  const [isVisible, setIsVisible] = useState(false);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  // When animation is complete
  const exitComplete = () => {
    ctaRef.current?.focus();
  };

  // Make visual buttons work like Buttons
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      setIsVisible(true);
    }
  };

  return (
    <>
      {fields.ctaDisplayText && (
        <Col className="pt-6">
          <LPLink
            aria-label={fields.ctaSheetLabel || 'Open dialog'}
            analyticsName={`cosmos-landing-page-cta-standalone-${fields?.analyticsId}`}
            ctaStyle={fields.ctaStyle}
            ctaDisplayText={fields.ctaDisplayText}
            tabIndex={0}
            onKeyDown={onKeyDown}
            ref={ctaRef}
            onClick={() => setIsVisible(true)}
          />
        </Col>
      )}
      <Sheet
        entry={fields.ctaSheet}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        exitComplete={exitComplete}
      />
    </>
  );
};

interface HeaderContentProps {
  fields: ILpComponentHeaderStandalone['fields'];
  componentTheme: IComponentThemeOptions;
  entry: ILpComponentHeaderStandalone;
  theme: IBNComponentThemeConfig;
}

const HeaderContent = ({ theme, fields, componentTheme, entry }: HeaderContentProps) => {
  const isCentered = fields.textAlign === 'Center';
  const isTwoColumns = fields.layout === 'Two Columns';

  return (
    <Container className="py-5 lg:py-10">
      <Col className="gap-7 md:flex-row">
        <Col className={cn('gap-4 md:flex-[6]', isTwoColumns ? 'md:basis-0' : 'md:basis-auto')}>
          {(fields.eyebrow || fields.title) && (
            <Col>
              {fields.eyebrow && (
                <Eyebrow textAlign={isCentered ? 'center' : 'auto'} color={theme.contentPrimary}>
                  {fields.eyebrow}
                </Eyebrow>
              )}
              {fields.title && (
                <TitleWithAccent
                  title={fields.title}
                  titleAccent={fields.titleAccent}
                  titleSize={fields.titleSize}
                  textAlign={isCentered ? 'center' : 'auto'}
                  marginHorizontal={isCentered ? 'auto' : 'unset'}
                  titleColor={theme.contentPrimary}
                  titleAccentColor={theme.contentPrimaryAccent}
                  overRideTitleTag="h1"
                />
              )}
            </Col>
          )}
          <Col className={cn('gap-4', isCentered && 'self-center')}>
            {(fields.subhead || (!isTwoColumns && fields.desc)) && (
              <Col>
                {fields.subhead &&
                  (fields.titleSize === 'Large' ? (
                    <Subhead
                      mb={0} // @ts-expect-error maxWidth ch required
                      $largerThanMD={{ maxWidth: '48ch' }}
                      textAlign={isCentered ? 'center' : 'auto'}
                    >
                      {fields.subhead}
                    </Subhead>
                  ) : (
                    <SubheadSmall
                      mb={0} // @ts-expect-error maxWidth ch required
                      $largerThanMD={{ maxWidth: '48ch' }}
                      textAlign={isCentered ? 'center' : 'auto'}
                    >
                      {fields.subhead}
                    </SubheadSmall>
                  ))}
                {!isTwoColumns && fields.desc && (
                  <Desc textAlign={isCentered ? 'center' : 'auto'}>{fields.desc}</Desc>
                )}
              </Col>
            )}
            {fields.ctaDisplayText && fields.ctaUrl && (
              <LPLink
                aria-label={`Go to page ${fields.ctaDisplayText}`}
                analyticsName={`cosmos-landing-page-cta-standalone-${fields?.analyticsId}`}
                ctaStyle={fields.ctaStyle}
                ctaDisplayText={fields.ctaDisplayText}
                ctaUrl={fields.ctaUrl}
                className={isCentered ? 'w-max self-center' : 'w-max'}
              />
            )}

            {fields.ctaDisplayText && fields.ctaSheet && <CtaSheet entry={entry} />}

            {fields.contextualField && (
              <Box className={cn('w-full', isCentered ? 'justify-center' : 'justify-start')}>
                <ContextualField
                  theme={componentTheme}
                  contextFields={fields.contextualField}
                  inputLocation="header-standalone"
                />
              </Box>
            )}
          </Col>
        </Col>
        {isTwoColumns && fields.desc && (
          <Col className="self-end md:flex-[4] md:basis-0 lg:mr-10">
            <Desc textAlign={isCentered ? 'center' : 'auto'}>{fields.desc}</Desc>
          </Col>
        )}
      </Col>
    </Container>
  );
};

const RenderHeaderStandalone = (entry: ILpComponentHeaderStandalone) => {
  const { fields } = entry;
  const componentTheme = getComponentThemeColors(fields.backgroundColor);

  const themeKey =
    fields.backgroundColor in BNColorThemes
      ? (fields.backgroundColor as BNColorThemes)
      : BNColorThemes.Light;
  const layoutThemeKey =
    fields.layoutBackgroundColor && fields.layoutBackgroundColor in BNColorThemes
      ? (fields.layoutBackgroundColor as BNColorThemes)
      : BNColorThemes.Light;
  const theme = getBNComponentThemeColors(themeKey);

  return (
    <Col className={cn('relative lp-section', BG_CLASS[layoutThemeKey])}>
      <Col>{fields.showArch && <ArchSVG color={BG_CSS_VAR[themeKey]} />}</Col>
      <Col className={cn('relative', BG_CLASS[themeKey])}>
        {fields.showGradientBackground ? (
          <SpinningGradient>
            <HeaderContent
              theme={theme}
              fields={fields}
              componentTheme={componentTheme}
              entry={entry}
            />
          </SpinningGradient>
        ) : (
          <HeaderContent
            theme={theme}
            fields={fields}
            componentTheme={componentTheme}
            entry={entry}
          />
        )}
      </Col>
    </Col>
  );
};

const HeaderStandalone: EntryComponent<ILpComponentHeaderStandalone> = {
  render: RenderHeaderStandalone,
};

export default HeaderStandalone;
